import { Component, OnInit, ViewEncapsulation, Input, Output, AfterViewInit, HostListener, Renderer, ElementRef, forwardRef, OnDestroy, EventEmitter,Inject} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import {SettingsService} from "@app/core";

@Component({
    selector: 'cs-timepicker',
    encapsulation: ViewEncapsulation.Emulated,
    templateUrl: './cs-timepicker.component.html',
    styles: [''],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CsTimepickerComponent),
            multi: true
        }
    ]
})
export class CsTimepickerComponent implements OnInit, ControlValueAccessor {
    _value: Date;
    private _onTouched: (_?: any) => void = () => { };
    private _onChange: (_?: any) => void = () => { };
    unregister: Function;
    @Output() hourEvent = new EventEmitter<string>();
    @Output() minuteEvent  = new EventEmitter<string>();
    @Input() minutesDuration: number = 15;
    @Input() containerWidth: string;
    constructor(
        private el_ref: ElementRef,
        private renderer: Renderer,
        @Inject(forwardRef(() => SettingsService)) private settings
    ) { }

    @Output('valueChanged') valueEmitter: EventEmitter<any> = new EventEmitter();

    // Set Touched on blur
    onBlur() {
        this._onTouched();
    }

    // From ControlValueAccessor Interface
        writeValue(value: any) {
        if (value !== this._value) {
            if (value instanceof Date) this._value = value;
            else if (typeof (value) == 'string') {
                this._value = new Date(value);
                setTimeout(() => this.updateModelValue(), 10);
            }
            else {
                this._value = new Date();
                setTimeout(() => this.updateModelValue(), 10);
            }
            this.assignValues();
        }
    }

    // From ControlValueAccessor Interface
    registerOnChange(fn: (_: any) => void) {
        this._onChange = fn;
    }

    // From ControlValueAccessor Interface
    registerOnTouched(fn: (_: any) => void) {
        this._onTouched = fn;
    }

    // Publish updated value to form control
    updateModelValue() {
        this._onChange(this._value);
        this.emitValue(this._value);
    }

    // Timepicker functions
    hoursDropdownItems: string[] = [];
    minutesDropdownItems: string[] = this.generateMinuteArray(this.minutesDuration);
    hour: string;
    minute: string;
    second: number = 0;
    meridian: 'AM' | 'PM';
    private eventsSubscription: Subscription;
    @Input() timepickerChangeEvent: Observable<any>;
    _showMeridian: boolean = true;
    @Input("meridian")
    set showMeridian(val: boolean) {
        this._showMeridian = !!val;

        // 01-12 with meridian (00:xx is shown as 12 AM), 00-23 without
        this.hoursDropdownItems = this._showMeridian
            ? (new Array(12)).join('#').split('#').map((h, index) => this.pad(index + 1))
            : (new Array(24)).join('#').split('#').map((h, index) => this.pad(index));
    }

    ngOnInit() {
        this._value = new Date();
        const intlSettings = this.settings.getIntlSettings();
        this._showMeridian = parseInt(intlSettings['time']) === 0 ;
        if(this.timepickerChangeEvent){
            this.eventsSubscription = this.timepickerChangeEvent.subscribe((data:any) => {
                if(data){
                    if(data.hasOwnProperty('hour')){
                        this.hour = data.hour;
                        this.onHourChange();
                    }
                    else if(data.hasOwnProperty('min')){
                        this.minute = data.min;
                        this.onMinuteChange();
                    }
                }
            });
        }
        this.showMeridian = this._showMeridian;
        this.minutesDropdownItems = this.generateMinuteArray(this.minutesDuration);
    }

    // Assigning values to the component variables
    assignValues() {
        if (this._showMeridian) {
            this.meridian = this._value.getHours() >= 12 ? 'PM' : 'AM';
            // 00:xx is shown as 12:xx AM, 12:xx as 12:xx PM
            this.hour = this.pad(this._value.getHours() % 12 === 0 ? 12 : this._value.getHours() % 12);
        } else {
            this.hour = this.pad(this._value.getHours());
        }
        this.minute = this.pad(this._value.getMinutes());
    }

    // Add leading zero
    pad(n) {
        return ("00" + n).slice(-2);
    }

    generateMinuteArray(duration: number): string[] {
        const result: string[] = [];

        for (let i = 0; i < 60; i += duration) {
            result.push(i < 10 ? `0${i}` : `${i}`);
        }

        return result;
    }

    onHourChange() {
        this._value.setHours(this.getHoursIn24());
        this._onTouched();
        this.updateModelValue();
        this.hourEvent.emit((this._showMeridian && this.meridian === 'PM' ? parseInt(this.hour) + 12 : parseInt(this.hour)).toString());
    }

    onMinuteChange() {
        this._value.setMinutes(parseInt(this.minute));
        this._onTouched();
        this.updateModelValue();
        this.minuteEvent.emit(this.minute);
    }

    onMeridianChange() {
        this._value.setHours(this.getHoursIn24());

        this._onTouched();
        this.updateModelValue();
        this.hourEvent.emit((this._showMeridian && this.meridian === 'PM' ? parseInt(this.hour) + 12 : parseInt(this.hour)).toString());
        this.minuteEvent.emit(this.minute);
    }

    // Selected hour converted to 0-23. Never returns 24, as setHours(24) rolls the date over to the next day
    getHoursIn24(): number {
        let hours = parseInt(this.hour);
        if (this._showMeridian) {
            hours = hours % 12;
            if (this.meridian === 'PM') hours += 12;
        }
        return hours;
    }

    emitValue(value) {
        this.valueEmitter.emit(value);
    }
}
